import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { delay } from '@libs/utils/helpers/delay';
import { safeSessionStorage } from '@consumidor-positivo/ts-utils';

import { LoaderCircular } from '@common/components/LoaderCircular';
import { pageNames, RouteMap } from '@common/constants/RouteMap';
import { useTracking } from '@common/hooks/useTracking';
import { useUserContext } from '@common/contexts/UserContext';
import useDatadogRum from '@libs/utils/hooks/datadog/useDatadogRum';

import PublicRoutes from '@common/routes/PublicRoutes';
import PrivateRoutes from '@common/routes/PrivateRoutes';
import PartnerPrivateRoutes from '@common/routes/PartnerPrivateRoutes';
import MFAPositiveDataRoutes from '@common/routes/MFAPositiveDataRoutes';

import { Redirect } from '@common/components/Redirect';

import { useAdmissionRouter } from '@admission/routes/Router';
import { useBillsRouter } from '../../bills/routes/Router';
import { Router as useTallyRouter } from '@tally/routes/Router';
import { Router as useHubRouter } from '@hub/routers/Router';
import { Router as useDuesRouter } from '@dues/routes/Router';
import ErrorBoundary from '@common/components/ErrorBoundary';
import { GlobalError } from '@common/pages/GlobalError';

import { HomeDefaultTemplate } from '@common/Templates/HomeDefault';

import useNavigationGuard from '@libs/acp/hooks/useNavigationGuard';
import { useRenegFallback } from '@common/hooks/useRenegFallback';
import useRenegAppExperiment from '@common/abtest/experiments/useRenegAppExperiment';

const Dashboard = lazy(() => import('../pages/Dashboard'));
const CpfAlert = lazy(() => import('../pages/CpfAlert'));
const ActivityHistory = lazy(() => import('../pages/CpfAlert/ActivityHistory'));
const NotFound = lazy(() => import('../pages/NotFound'));
const MyScore = lazy(() => import('../pages/MyScore'));
const MyOffers = lazy(() => import('../pages/MyOffers'));
const MyCpf = lazy(() => import('../pages/MyCpf'));

const ACPMFAPositiveData = lazy(() => import('../pages/ACP/MFAPositiveData'));

const UnlockProtectedFeatureACP = lazy(() => import('@admission/pages/ACP/UnlockProtectedFeature'));
const UnlockProtectedFeatureSuccessACP = lazy(() => import('@admission/pages/ACP/Success/UnlockProtectedFeatureSuccess'));
const UnlockProtectedFeatureRecoveryPasswordACP = lazy(() => import('@admission/pages/ACP/RecoveryPassword'));

const UnlockProtectedFeatureInvalidKbaACP = lazy(() => import('@admission/pages/ACP/Error/InvalidResetPasswordKBA'));
const UnlockProtectedFeatureBlockedKbaACP = lazy(() => import('@admission/pages/ACP/Error/BlockedResetPasswordKBA'));

const Router: React.FC = () => {
  const { admissionPrivatePages, admissionPublicPages, partnerPrivatePages } = useAdmissionRouter();
  const { signOut } = useUserContext();
  const pagesDues = useDuesRouter();
  const pagesTally = useTallyRouter();
  const pagesHub = useHubRouter();
  const pagesBills = useBillsRouter();

  const { isRenegFallback } = useRenegFallback();
  const { getRedirectUrl } = useRenegAppExperiment();

  const location = useLocation();

  const { pageViewEvent } = useTracking();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const utmTerm = searchParams.get('utm_term');
  if (utmTerm) {
    safeSessionStorage.setItem('utm_term', utmTerm);
  }

  //#region RenegAC
  const showHubModal = !(!isRenegFallback && window.location.pathname === RouteMap.MyCpf);

  const RedirectFlowAc = () => {
    useEffect(() => {
      delay(1000).then(() => {
        window.location.href = getRedirectUrl('debts');
      });
    }, []);

    return <LoaderCircular fullPage />;
  };
  //#endregion

  const ACPNavigationObserver = () => {
    useNavigationGuard();
    return null;
  };

  useEffect(() => {
    window?.scrollTo(0, 0);
    delay(150).then(() => pageViewEvent());
  }, [location]);

  useEffect(() => {
    if (location.pathname === RouteMap.Logout) {
      signOut(true);
    }
  }, [location.pathname]);

  useDatadogRum({
    applicationId: import.meta.env.VITE_DD_RUM_APP_ID,
    clientToken: import.meta.env.VITE_DD_RUM_TOKEN,
    version: import.meta.env.VITE_DEPLOY_VERSION,
    service: 'app.consumidorpositivo.com.br',
    sessionSampleRate: 1,
    sessionReplaySampleRate: 1,
    traceSampleRate: 10,
    allowedTracingUrls: [
      /https:\/\/.*\.consumidorpositivo\.com.br/,
      /https:\/\/.*\.acordocerto\.com.br/,
      'https://cdn.acordocerto.net',
    ],
  });

  return (
    <ErrorBoundary fallback={(error) => <GlobalError error={error} />}>
      <Suspense fallback={<LoaderCircular fullPage />}>
        <ACPNavigationObserver />
        <Routes>
          <Route element={<PublicRoutes />}>
            {admissionPublicPages}
            <Route path={RouteMap.Help} element={<Redirect to="https://www.consumidorpositivo.com.br/central-de-ajuda" />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            {admissionPrivatePages}
            {pagesTally}
            {pagesHub}
            {pagesBills}
            {pagesDues}
            <Route path={RouteMap.Dashboard} element={<Dashboard />} />
            
						<Route element={<MFAPositiveDataRoutes />}>
							<Route path={RouteMap.CpfAlert} element={<CpfAlert />} />
						</Route>

            <Route path={RouteMap.ActivityHistory} element={<ActivityHistory />} />
            {/* //TODO: migrar pra CP usar pageNames - mesma estrutura do AC */}
            <Route path={pageNames.unlockProtectedFeature.path} element={<UnlockProtectedFeatureACP />} />
            <Route path={pageNames.unlockProtectedFeatureSuccess.path} element={<UnlockProtectedFeatureSuccessACP />} />
            <Route path={pageNames.unlockProtectedFeaturePassword.path} element={<UnlockProtectedFeatureRecoveryPasswordACP />} />
            <Route path={pageNames.unlockProtectedFeatureACPInvalidKBA.path} element={<UnlockProtectedFeatureInvalidKbaACP />} />
            <Route path={pageNames.unlockProtectedFeatureACPBlockedKBA.path} element={<UnlockProtectedFeatureBlockedKbaACP />} />
            <Route element={<HomeDefaultTemplate showHubModal={showHubModal} />}>
              <Route path={RouteMap.MyScore} element={<MyScore />} />
              <Route path={RouteMap.MyOffers} element={<MyOffers />} />

							<Route element={<MFAPositiveDataRoutes />}>
								<Route path={RouteMap.MyCpf} element={!isRenegFallback ? <RedirectFlowAc /> : <MyCpf />} />
							</Route>
            </Route>
						
						<Route path={RouteMap.MFAPositiveData} element={<ACPMFAPositiveData />} />
          </Route>

          <Route element={<PartnerPrivateRoutes />}>{partnerPrivatePages}</Route>
          <Route path="/*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default Router;
